import { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar/Navbar';
import Sidebar from '../../components/Sidebar/Sidebar';
import HumanBody from '../../components/HumanBody/HumanBody';
import { fetchDashboardStats } from '../../api/reportService';

const Dashboard = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [mainStats, setMainStats] = useState(null);
  const [medicationStats, setMedicationStats] = useState([]);
  const [attackLocations, setAttackLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('12months');
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Fetch dashboard statistics from API
  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const options = useCustomRange
        ? { startDate, endDate }
        : { timePeriod: selectedPeriod };

      const response = await fetchDashboardStats(options);

      if (response.success) {
        setMainStats(response.data.mainStats);
        setAttackLocations(response.data.attackLocations);
        setMedicationStats(response.data.medicationStats);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
      setError(err.message || 'Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount and when preset period changes (not for custom dates)
  useEffect(() => {
    if (!useCustomRange) {
      loadDashboardStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, useCustomRange]);

  // Handle time period change (preset periods)
  const handlePeriodChange = (e) => {
    setSelectedPeriod(e.target.value);
    setUseCustomRange(false);
  };

  // Handle custom range toggle
  const handleCustomRangeToggle = () => {
    setUseCustomRange(!useCustomRange);
    if (!useCustomRange) {
      // Set default dates when enabling custom range
      const end = new Date();
      const start = new Date();
      start.setMonth(start.getMonth() - 12);

      setEndDate(end.toISOString().split('T')[0]);
      setStartDate(start.toISOString().split('T')[0]);
    }
  };

  // Handle date changes
  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  const MainStatCard = ({ value, subValue, label, icon }) => (
    <div className="bg-white p-4 rounded-lg shadow">
      <div className="flex items-center">
        <div className="p-3 bg-accent/10 rounded-full">
          <i className={`fas ${icon} text-primary text-xl`}></i>
        </div>
        <div className="ml-4">
          <div className="flex items-baseline">
            <span className="text-2xl font-bold text-primary">{value}</span>
            {subValue && <span className="ml-2 text-sm text-neutral">{subValue}</span>}
          </div>
          <p className="text-sm text-neutral mt-1">{label}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-light">
      <Sidebar isOpen={isSidebarOpen} />

      <div className={`${isSidebarOpen ? 'ml-64' : 'ml-0'} transition-margin duration-300 ease-in-out`}>
        <Navbar toggleSidebar={toggleSidebar} />

        <main className="p-4">
          {children || (
            <div className="p-6">
              {/* Header Section */}
              <div className="mb-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h1 className="text-2xl font-bold text-primary">Dashboard Overview</h1>
                    <p className="text-neutral">
                      {useCustomRange
                        ? `Custom Range: ${startDate} to ${endDate}`
                        : selectedPeriod === '3months'
                        ? 'Last 3 Months Statistics'
                        : selectedPeriod === '6months'
                        ? 'Last 6 Months Statistics'
                        : 'Last 12 Months Statistics'}
                    </p>
                  </div>
                  <button
                    onClick={handleCustomRangeToggle}
                    className={`px-4 py-2 rounded-lg transition ${
                      useCustomRange
                        ? 'bg-primary text-white'
                        : 'bg-white border border-light text-primary hover:bg-light'
                    }`}
                    disabled={loading}
                  >
                    <i className="fas fa-calendar-alt mr-2"></i>
                    {useCustomRange ? 'Use Preset Range' : 'Custom Date Range'}
                  </button>
                </div>

                {/* Filter Controls */}
                <div className="bg-white rounded-lg shadow p-4">
                  {!useCustomRange ? (
                    // Preset Time Period Selector
                    <div className="flex items-center gap-3">
                      <label className="text-sm font-medium text-neutral">Time Period:</label>
                      <select
                        className="border border-light rounded-lg px-4 py-2 text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                        value={selectedPeriod}
                        onChange={handlePeriodChange}
                        disabled={loading}
                      >
                        <option value="12months">Last 12 Months</option>
                        <option value="6months">Last 6 Months</option>
                        <option value="3months">Last 3 Months</option>
                      </select>
                    </div>
                  ) : (
                    // Custom Date Range Pickers
                    <div>
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                          <label className="text-sm font-medium text-neutral">Start Date:</label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={handleStartDateChange}
                            disabled={loading}
                            max={endDate || new Date().toISOString().split('T')[0]}
                            className="border border-light rounded-lg px-3 py-2 text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm font-medium text-neutral">End Date:</label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={handleEndDateChange}
                            disabled={loading}
                            min={startDate}
                            max={new Date().toISOString().split('T')[0]}
                            className="border border-light rounded-lg px-3 py-2 text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <button
                          onClick={loadDashboardStats}
                          disabled={
                            loading ||
                            !startDate ||
                            !endDate ||
                            new Date(startDate) > new Date(endDate)
                          }
                          className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <i className="fas fa-search mr-2"></i>
                          Apply Filter
                        </button>
                      </div>
                      {/* Validation message */}
                      {startDate && endDate && new Date(startDate) > new Date(endDate) && (
                        <p className="text-red-600 text-sm mt-2">
                          <i className="fas fa-exclamation-circle mr-1"></i>
                          Start date must be before or equal to end date
                        </p>
                      )}
                      {/* Info message when dates are selected but not applied */}
                      {startDate && endDate && new Date(startDate) <= new Date(endDate) && !loading && (
                        <p className="text-blue-600 text-sm mt-2">
                          <i className="fas fa-info-circle mr-1"></i>
                          Click "Apply Filter" to load statistics for the selected date range
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Loading State */}
              {loading && (
                <div className="flex justify-center items-center py-12">
                  <div className="text-center">
                    <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
                    <p className="text-neutral">Loading dashboard statistics...</p>
                  </div>
                </div>
              )}

              {/* Error State */}
              {error && !loading && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <i className="fas fa-exclamation-triangle text-red-600 text-xl mr-3 mt-1"></i>
                    <div>
                      <h3 className="text-red-800 font-semibold mb-1">Error Loading Dashboard</h3>
                      <p className="text-red-700">{error}</p>
                      <button
                        onClick={loadDashboardStats}
                        className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Main Content - Only show when not loading */}
              {!loading && !error && mainStats && (
                <>
                  {/* Main Stats Section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    {Object.entries(mainStats).map(([key, stat]) => (
                      <MainStatCard key={key} {...stat} />
                    ))}
                  </div>

                  {/* Attack Locations Section */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <div className="bg-white rounded-lg shadow p-6">
                      <h2 className="text-lg font-semibold text-primary mb-4">Attack Locations</h2>
                      <div className="flex justify-center items-center h-[700px]">
                        {attackLocations.length > 0 ? (
                          <HumanBody localizedData={attackLocations} />
                        ) : (
                          <p className="text-neutral">No attack location data available</p>
                        )}
                      </div>
                    </div>

                    {/* Medications Stats Section */}
                    <div className="bg-white rounded-lg shadow p-6">
                      <h2 className="text-lg font-semibold text-primary mb-4">Medication Statistics</h2>
                      <div className="grid grid-cols-1 gap-4">
                        {medicationStats.length > 0 ? (
                          (() => {
                            // Medications to exclude from display
                            const excludedMedications = ['cinryze'];
                            // Filter out excluded medications
                            const filteredMedicationStats = medicationStats.filter(
                              m => !excludedMedications.includes(m.name.toLowerCase())
                            );
                            // Calculate max usage count for progress bar scaling
                            const maxUsageCount = Math.max(...filteredMedicationStats.map(m => m.usageCount));

                            return filteredMedicationStats.map((med, index) => {
                              // Calculate progress percentage based on usage count
                              const progressPercentage = (med.usageCount / maxUsageCount) * 100;

                              // Determine if trend is positive or negative
                              const isPositiveTrend = med.trend && med.trend.startsWith('+');
                              const trendColor = isPositiveTrend ? 'text-green-600' : 'text-red-600';
                              const barColor = isPositiveTrend ? 'bg-green-500' : 'bg-red-500';

                              return (
                                <div key={index} className="border border-light rounded-lg p-4">
                                  <div className="flex justify-between items-start mb-2">
                                    <span className="text-sm font-medium text-neutral">{med.name}</span>
                                    {/* <span className={`text-xs font-semibold ${trendColor}`}>
                                      {med.trend}
                                    </span> */}
                                  </div>
                                  <div className="text-xl font-bold text-primary">{med.value}</div>
                                  <div className="mt-3 w-full bg-light rounded-full h-2">
                                    <div
                                      className={`h-2 rounded-full ${barColor} transition-all duration-300`}
                                      style={{ width: `${progressPercentage}%` }}
                                    ></div>
                                  </div>
                                </div>
                              );
                            });
                          })()
                        ) : (
                          <p className="text-neutral text-center py-8">No medication data available</p>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard; 